import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TabGroup,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@headlessui/react';
import type { OpportunityDef, Patient } from '@/lib/types';
import {
  CHANNEL_LABEL,
  PROMPT_HINT_CHIPS,
  generateMessage,
  type CampaignChannel,
  type GeneratedMessage,
} from '@/lib/messageTemplates';
import { formatLastEvaluation } from '@/lib/signals';

interface CampaignDrawerProps {
  def: OpportunityDef | null;
  patients: Patient[];
  onClose: () => void;
  onLaunch?: (channel: CampaignChannel) => void;
}

const tabClass = ({ selected }: { selected: boolean }) =>
  [
    'font-inherit text-[12.5px] px-3 py-2.5 -mb-px border-b-2 cursor-pointer outline-none flex items-center gap-1.5 transition-colors hover:text-primary focus-visible:text-primary',
    selected
      ? 'text-primary font-semibold border-b-primary'
      : 'text-tertiary font-medium border-b-transparent',
  ].join(' ');

export function CampaignDrawer({
  def,
  patients,
  onClose,
  onLaunch,
}: CampaignDrawerProps) {
  const [channel, setChannel] = useState<CampaignChannel>('whatsapp');
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [seed, setSeed] = useState(0);
  const [message, setMessage] = useState<GeneratedMessage>({ body: '' });
  const [launching, setLaunching] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!def) {
      setLaunching(false);
      setShowSuccess(false);
      setTabIndex(0);
      setSelectedIds(new Set());
      return;
    }
    setPrompt('');
    setSeed(0);
    setChannel('whatsapp');
    setMessage(generateMessage(def.kind, 'whatsapp', '', 0));
    setTabIndex(0);
    setSelectedIds(new Set(patients.map((p) => p.id)));
  }, [def, patients]);

  useEffect(() => {
    if (!def) return;
    setMessage(generateMessage(def.kind, channel, prompt, seed));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  const handleGenerate = () => {
    if (!def) return;
    setGenerating(true);
    const nextSeed = seed + 1;
    window.setTimeout(() => {
      setMessage(generateMessage(def.kind, channel, prompt, nextSeed));
      setSeed(nextSeed);
      setGenerating(false);
    }, 1200);
  };

  const handleAddHint = (value: string) => {
    setPrompt((p) => (p ? `${p}, ${value}` : value));
  };

  const togglePatient = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === patients.length) return new Set();
      return new Set(patients.map((p) => p.id));
    });
  };

  const handlePrimary = () => {
    if (launching) return;
    if (tabIndex === 0) {
      setTabIndex(1);
      return;
    }
    if (selectedIds.size === 0) return;
    setLaunching(true);
    setShowSuccess(true);
    window.setTimeout(() => {
      if (onLaunch) onLaunch(channel);
      else onClose();
    }, 2400);
  };

  const allSelected = selectedIds.size === patients.length && patients.length > 0;
  const someSelected = selectedIds.size > 0 && !allSelected;

  return (
    <Dialog
      open={!!def}
      onClose={onClose}
      className="ai-drawer-fade-slow fixed inset-0 z-[100] bg-black/30 dark:bg-black/55 flex justify-end p-3.5 max-[760px]:p-2"
    >
      <DialogPanel className="ai-drawer-slide-slow ai-edge relative w-full max-w-[460px] h-full ai-bg backdrop-blur-xl backdrop-saturate-150 rounded-2xl flex flex-col overflow-hidden">
        <div aria-hidden="true" className="ai-glow absolute inset-0 pointer-events-none" />
        <div aria-hidden="true" className="ai-grid absolute inset-0 pointer-events-none" />
        {def && (
          <div className="ai-drawer-content relative flex flex-col flex-1 min-h-0">
            <header className="flex items-center gap-3 px-6 py-4 border-b border-line-glass shrink-0">
              <div className="flex-1 min-w-0">
                <DialogTitle as="h2" className="text-[16px] font-semibold tracking-[-0.01em] m-0 text-primary leading-[1.3] truncate">
                  {def.title}
                </DialogTitle>
                <p className="text-[12px] text-tertiary m-0 mt-0.5">
                  {patients.length} paciente{patients.length === 1 ? '' : 's'}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                className="text-[13px] w-7 h-7 border border-line-glass bg-transparent text-tertiary rounded-sm cursor-pointer transition-colors hover:border-line-default hover:text-primary shrink-0"
              >
                ✕
              </button>
            </header>

            <TabGroup
              selectedIndex={tabIndex}
              onChange={setTabIndex}
              className="flex-1 flex flex-col min-h-0"
            >
              <TabList className="flex gap-1 px-5 pt-1.5 border-b border-line-glass shrink-0">
                <Tab className={tabClass}>Mensaje</Tab>
                <Tab className={tabClass}>
                  Destinatarios{' '}
                  <span className="text-[11px] font-semibold text-muted border border-line-glass px-1.5 py-px rounded-full tnum data-selected:border-line-default data-selected:text-secondary">
                    {selectedIds.size}/{patients.length}
                  </span>
                </Tab>
              </TabList>

              <TabPanels className="flex-1 min-h-0 flex flex-col">
                <TabPanel className="flex-1 px-6 py-5 overflow-auto flex flex-col gap-6 outline-none">
                  <Section label="Canal">
                    <div className="flex gap-1.5">
                      {(['whatsapp', 'email'] as const).map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setChannel(c)}
                          className={`text-[12.5px] font-medium px-3 py-1.5 rounded-md border cursor-pointer transition-colors ${
                            channel === c
                              ? 'bg-primary text-surface border-primary'
                              : 'bg-transparent text-secondary border-line-glass hover:border-line-default hover:text-primary'
                          }`}
                        >
                          {CHANNEL_LABEL[c]}
                        </button>
                      ))}
                    </div>
                  </Section>

                  <Section label={<><SparkleIcon /> Asistente IA</>}>
                    <textarea
                      rows={3}
                      placeholder="Describe el tono, enfoque o instrucciones (ej: cálido, breve, enfocado en agendar)"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      className="font-inherit text-[13px] text-primary border border-line-glass rounded-md bg-transparent px-3 py-2.5 resize-y outline-none transition-colors leading-[1.5] w-full placeholder:text-faint focus:border-line-default"
                    />
                    <div className="flex flex-wrap gap-1.5">
                      {PROMPT_HINT_CHIPS.map((c) => (
                        <button
                          key={c.label}
                          type="button"
                          onClick={() => handleAddHint(c.value)}
                          className="font-inherit text-[11px] font-medium px-2.5 py-1 rounded-full border border-line-glass bg-transparent text-tertiary cursor-pointer transition-colors hover:border-line-default hover:text-primary"
                        >
                          + {c.label}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={handleGenerate}
                      disabled={generating}
                      className="self-start inline-flex items-center gap-1.5 font-inherit text-[12.5px] font-medium px-3 py-1.5 rounded-md bg-transparent border border-line-glass text-secondary cursor-pointer transition-colors hover:border-line-default hover:text-primary disabled:opacity-60 disabled:cursor-progress mt-1"
                    >
                      <SparkleIcon />
                      {generating ? 'Generando…' : 'Generar mensaje'}
                    </button>
                  </Section>

                  <Section label="Mensaje">
                    {channel === 'email' && message.subject && (
                      <input
                        value={message.subject}
                        onChange={(e) =>
                          setMessage((m) => ({ ...m, subject: e.target.value }))
                        }
                        placeholder="Asunto"
                        className="font-inherit text-[13px] text-primary border border-line-glass rounded-md bg-transparent px-3 py-2 outline-none transition-colors w-full placeholder:text-faint focus:border-line-default"
                      />
                    )}
                    <textarea
                      rows={channel === 'email' ? 8 : 6}
                      value={message.body}
                      onChange={(e) =>
                        setMessage((m) => ({ ...m, body: e.target.value }))
                      }
                      disabled={generating}
                      className="font-inherit text-[13px] text-primary border border-line-glass rounded-md bg-transparent px-3 py-2.5 resize-y outline-none transition-colors leading-[1.5] w-full placeholder:text-faint focus:border-line-default disabled:opacity-70"
                    />
                    <p className="text-[11px] text-muted m-0 leading-[1.45]">
                      Variables como{' '}
                      <code className="font-mono text-[11px] border border-line-glass bg-transparent px-1 py-px rounded text-secondary">
                        {'{nombre}'}
                      </code>{' '}
                      se sustituyen al enviar. La infraestructura de envío aún no
                      está conectada — este flujo guarda el borrador localmente.
                    </p>
                  </Section>
                </TabPanel>

                <TabPanel className="flex-1 px-6 py-5 overflow-auto flex flex-col gap-3 outline-none">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted m-0">
                      Confirma a quién enviar ({selectedIds.size}/{patients.length})
                    </h3>
                    <button
                      type="button"
                      onClick={toggleAll}
                      className="text-[11px] font-medium text-tertiary bg-transparent border-0 cursor-pointer hover:text-primary transition-colors"
                    >
                      {allSelected ? 'Quitar todos' : 'Seleccionar todos'}
                    </button>
                  </div>
                  <ul className="list-none m-0 p-0 flex flex-col border border-line-glass bg-transparent rounded-md overflow-hidden">
                    {patients.map((p) => {
                      const checked = selectedIds.has(p.id);
                      return (
                        <li
                          key={p.id}
                          className="border-b border-line-glass last:border-b-0"
                        >
                          <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors hover:bg-hover">
                            <Checkbox
                              checked={checked}
                              onChange={() => togglePatient(p.id)}
                              indeterminate={false}
                            />
                            <div className="flex-1 min-w-0">
                              <div className={`text-[13px] font-semibold tracking-[-0.005em] truncate transition-colors ${checked ? 'text-primary' : 'text-tertiary'}`}>
                                {p.name}
                              </div>
                              <div className={`text-[11px] tnum transition-colors ${checked ? 'text-tertiary' : 'text-muted'}`}>
                                {p.age} años · {p.gender} · {p.city} · última eval.{' '}
                                {formatLastEvaluation(p.lastEvaluationAt).toLowerCase()}
                              </div>
                            </div>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                  {someSelected && (
                    <p className="text-[11px] text-muted m-0 leading-[1.45]">
                      {patients.length - selectedIds.size} paciente
                      {patients.length - selectedIds.size === 1 ? '' : 's'} excluido
                      {patients.length - selectedIds.size === 1 ? '' : 's'} de este envío.
                    </p>
                  )}
                </TabPanel>
              </TabPanels>
            </TabGroup>

            <footer className="flex justify-end items-center gap-2 px-6 py-3.5 border-t border-line-glass ai-bg backdrop-blur-md shrink-0">
              <button
                type="button"
                onClick={onClose}
                disabled={launching}
                className="text-[13px] font-medium text-tertiary bg-transparent border-0 px-3 py-2 rounded-md cursor-pointer transition-colors hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handlePrimary}
                disabled={
                  launching ||
                  patients.length === 0 ||
                  (tabIndex === 1 && selectedIds.size === 0)
                }
                className="font-inherit text-[13px] font-medium px-3.5 py-2 rounded-md bg-primary text-surface border-0 cursor-pointer transition-opacity hover:opacity-90 active:translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
              >
                {tabIndex === 0
                  ? `Revisar ${patients.length} destinatario${patients.length === 1 ? '' : 's'} →`
                  : launching
                    ? 'Lanzando…'
                    : `Lanzar a ${selectedIds.size} destinatario${selectedIds.size === 1 ? '' : 's'}`}
              </button>
            </footer>
          </div>
        )}
        {showSuccess && (
          <div
            aria-hidden="true"
            className="absolute inset-0 z-50 grid place-items-center pointer-events-none"
          >
            <div className="ai-success-bloom absolute inset-0" />
            <div className="ai-success-rise relative flex flex-col items-center gap-4 text-primary">
              <span className="ai-check-pop grid place-items-center w-16 h-16 rounded-full border border-line-default text-primary">
                <CheckIcon />
              </span>
              <span className="text-[24px] font-medium tracking-[-0.018em]">
                Campaña enviada
              </span>
              <span className="text-[12.5px] text-tertiary">
                {selectedIds.size} destinatario{selectedIds.size === 1 ? '' : 's'} ·{' '}
                {CHANNEL_LABEL[channel]}
              </span>
            </div>
          </div>
        )}
      </DialogPanel>
    </Dialog>
  );
}

interface CheckboxProps {
  checked: boolean;
  onChange: () => void;
  indeterminate?: boolean;
}

function Checkbox({ checked, onChange }: CheckboxProps) {
  return (
    <span
      role="checkbox"
      aria-checked={checked}
      onClick={(e) => {
        e.preventDefault();
        onChange();
      }}
      className={`grid place-items-center w-4 h-4 rounded-sm border shrink-0 transition-colors ${
        checked
          ? 'bg-primary border-primary text-surface'
          : 'bg-transparent border-line-default'
      }`}
    >
      {checked && (
        <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 8l3.5 3.5L13 5" />
        </svg>
      )}
    </span>
  );
}

function CheckIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

interface SectionProps {
  label: React.ReactNode;
  children: React.ReactNode;
}

function Section({ label, children }: SectionProps) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted m-0 mb-0.5 inline-flex items-center gap-1.5 [&_svg]:text-secondary">
        {label}
      </h3>
      {children}
    </section>
  );
}

function SparkleIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 1.5l1.4 4.1 4.1 1.4-4.1 1.4L8 12.5 6.6 8.4 2.5 7l4.1-1.4L8 1.5z" />
      <path d="M13 11.5l.6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4-1.4-.6 1.4-.6.6-1.4z" />
    </svg>
  );
}
